import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Grid,
  Paper,
  Button,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: "#63c2e6",
  color: "#ffffff",
  "&:hover": {
    backgroundColor: "#4faed0",
  },
}));

function Profile({ user, laravelApiToken }) {
  const [userData, setUserData] = useState(user);
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch user data from API and set initial state
    const fetchUserData = async () => {
      try {
        const response = await fetch(
          `https://api.hackerthread.com/api/users/search?email=${user.signInDetails.loginId}`,
          {
            headers: {
              Authorization: `Bearer ${laravelApiToken}`,
            },
          }
        );
        const originalUserData = await response.json();
        console.log(originalUserData);
        setUserData(originalUserData.userDetails[0]);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user, laravelApiToken]);

  const handleSave = async () => {
    // Validate form fields
    if (newPassword !== confirmPassword) {
      setSnackbarMessage("Passwords do not match");
      setSnackbarOpen(true);
      return;
    }

    // Prepare data for API call
    const updateData = {
      name: name || userData.name,
      phone_number: phoneNumber || userData.phone_number,
    };

    setLoading(true);

    try {
      const response = await fetch(
        `https://api.hackerthread.com/api/users/${userData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${laravelApiToken}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (response.ok) {
        const updatedUserData = await response.json();
        setUserData(updatedUserData);
        setSnackbarMessage("Profile updated successfully");
        setSnackbarOpen(true);
        // Clear input fields after successful update
        setName("");
        setPhoneNumber("");
      } else {
        const errorData = await response.json();
        setSnackbarMessage(errorData.message || "Error updating profile");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setSnackbarMessage("Error updating profile");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ marginY: 4, pb: 6 }}>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>
      <StyledPaper>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Current Name:
              <Typography variant="subtitle1" gutterBottom>
                {userData.name}
              </Typography>
            </Typography>
            <TextField
              fullWidth
              label="New Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Email:
              <Typography variant="subtitle1" gutterBottom>
                {userData.email}
              </Typography>
            </Typography>
            <TextField fullWidth type="email" value={userData.email} disabled />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Current Phone Number:
              <Typography variant="subtitle1" gutterBottom>
                {userData.phone_number || "N/A"}
              </Typography>
            </Typography>
            <TextField
              fullWidth
              label="New Phone Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </Grid>
        </Grid>
      </StyledPaper>

      <StyledPaper>
        <Typography variant="h6" gutterBottom>
          Update Password
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Grid>
        </Grid>
      </StyledPaper>

      <Box display="flex" justifyContent="flex-end">
        <StyledButton
          variant="contained"
          disabled={loading}
          color="primary"
          onClick={handleSave}
        >
          {loading ? "Saving..." : "Save Changes"}
        </StyledButton>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={
            snackbarMessage.includes("successfully") ? "success" : "error"
          }
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Profile;
